// Persistence for the user's working context — the tenant, company and site chosen in the header.
//
// The context is saved per signed-in identity: two people sharing a browser must not inherit each
// other's tenant, and someone coming back to the app should land where they left off rather than on
// their default tenant. Records are therefore keyed by the Auth0 subject.
//
// `tenantId` is additionally mirrored to a top-level key of its own, because the axios request
// interceptor and the SWR cache-key helpers read it straight out of storage on every call and have
// no access to the React tree.

const USER_CONTEXT_KEY_PREFIX = 'weightrax.userContext.';

/** Shared with utils/axios and utils/tenantCacheKey, which read this key directly. */
export const TENANT_ID_STORAGE_KEY = 'tenantId';

// Pre-per-user record. Adopted once, by the first user to sign in after this change, so a live
// session keeps its selection across the upgrade; the flag then retires it for good, which stops
// the next user on the same browser from picking up the previous user's tenant.
const LEGACY_COMPANY_SITE_KEY = 'companySiteSelection';
const LEGACY_MIGRATED_KEY = 'weightrax.userContext.migrated';

export interface StoredUserContext {
  tenantId: number | null;
  companyId: number | null;
  companyName: string | null;
  siteId: number | null;
  siteName: string | null;
}

const EMPTY_USER_CONTEXT: StoredUserContext = {
  tenantId: null,
  companyId: null,
  companyName: null,
  siteId: null,
  siteName: null
};

/** Signed-out and pre-auth reads share one bucket; it is overwritten as soon as an identity is known. */
function storageKey(userKey: string | null): string {
  return `${USER_CONTEXT_KEY_PREFIX}${userKey ?? 'anonymous'}`;
}

function toId(value: unknown): number | null {
  const parsed = typeof value === 'string' ? Number(value) : value;
  return typeof parsed === 'number' && Number.isFinite(parsed) ? parsed : null;
}

function toName(value: unknown): string | null {
  return typeof value === 'string' && value.length > 0 ? value : null;
}

function readJson(key: string): Record<string, unknown> | null {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return null;

    const parsed = JSON.parse(raw);
    return parsed && typeof parsed === 'object' ? (parsed as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

/** The unscoped selection written by earlier versions, or null once it has been retired. */
function legacyUserContext(): StoredUserContext | null {
  try {
    if (localStorage.getItem(LEGACY_MIGRATED_KEY)) return null;

    const selection = readJson(LEGACY_COMPANY_SITE_KEY);
    const tenantId = toId(localStorage.getItem(TENANT_ID_STORAGE_KEY));

    if (tenantId == null && !selection) return null;

    return {
      tenantId,
      companyId: toId(selection?.companyId),
      companyName: toName(selection?.companyName),
      siteId: toId(selection?.siteId),
      siteName: toName(selection?.siteName)
    };
  } catch {
    return null;
  }
}

/**
 * The saved context for an identity, or an empty one when nothing has been saved yet. Falls back to
 * the legacy unscoped record until `completeLegacyMigration` retires it.
 */
export function loadUserContext(userKey: string | null): StoredUserContext {
  const stored = readJson(storageKey(userKey));

  if (stored) {
    return {
      tenantId: toId(stored.tenantId),
      companyId: toId(stored.companyId),
      companyName: toName(stored.companyName),
      siteId: toId(stored.siteId),
      siteName: toName(stored.siteName)
    };
  }

  return legacyUserContext() ?? EMPTY_USER_CONTEXT;
}

/**
 * Retires the legacy unscoped record. Call once the adopted values have been saved against an
 * identity, so no later sign-in can inherit them.
 */
export function completeLegacyMigration(): void {
  try {
    localStorage.removeItem(LEGACY_COMPANY_SITE_KEY);
    localStorage.setItem(LEGACY_MIGRATED_KEY, '1');
  } catch {
    // See saveUserContext.
  }
}

/** Writes the context for an identity, mirroring the tenant to the key non-React callers read. */
export function saveUserContext(userKey: string | null, context: StoredUserContext): void {
  try {
    localStorage.setItem(storageKey(userKey), JSON.stringify(context));

    if (context.tenantId == null) {
      localStorage.removeItem(TENANT_ID_STORAGE_KEY);
    } else {
      localStorage.setItem(TENANT_ID_STORAGE_KEY, String(context.tenantId));
    }
  } catch {
    // Storage can be unavailable (private browsing, quota); the in-memory context still works for
    // this session, so a failed save must not break the app.
  }
}
